import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '@theme/useTheme';
import GradientBackground from '@components/ui/GradientBackground';
import { useAuth } from '@contexts/AuthContext';
import LoginModal from '@components/modals/LoginModal';
import ProfileModal from '@components/modals/ProfileModal';
import { FaExternalLinkAlt, FaCalendarAlt, FaNewspaper } from 'react-icons/fa';
import Spinner from '@components/ui/Spinner';
import { TrackedLink, TrackedAnchor } from '@components/analytics/TrackedLink';
import { trackGtmButtonClick } from '@/analytics/gtm';

interface NewsArticle {
  title: string;
  description: string;
  url: string;
  urlToImage: string;
  publishedAt: string;
  source: {
    name: string;
  };
}

const News: React.FC = () => {
  const { colors } = useTheme();
  const { isAuthenticated } = useAuth();
  const [showProfile, setShowProfile] = useState(false);
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);

  const handleCloseProfile = () => {
    setShowProfile(false);
  };

  useEffect(() => {
    const handleLoginModal = () => window.dispatchEvent(new Event('openLoginModal'));
    const handleProfileModal = () => setShowProfile(true);

    window.addEventListener('openLoginModal', handleLoginModal);
    window.addEventListener('openProfileModal', handleProfileModal);

    return () => {
      window.removeEventListener('openLoginModal', handleLoginModal);
      window.removeEventListener('openProfileModal', handleProfileModal);
    };
  }, []);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setError(null);
    setLoading(true);
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    trackGtmButtonClick({
      button_id: 'news_load_more',
      section: 'page_news',
      label: 'Load More',
    });
    setCurrentPage(prev => prev + 1);
    setLoading(true);
    setError(null);
  };

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);

        const healthQuery = '"sleep apnea" OR "obstructive sleep apnea" OR "central sleep apnea" OR CPAP OR "sleep medicine" OR "sleep disorder" OR "sleep health" OR "sleep study" OR "polysomnography" OR "sleep research" OR "sleep therapy" OR "sleep treatment" OR "respiratory sleep disorder" OR "sleep breathing" OR "sleep clinic" OR "sleep specialist"';

        const apiKey = '8fbff4d924f245c38e8cd16eaf6a2264';

        let url = `https://newsapi.org/v2/everything?q=${encodeURIComponent(healthQuery)}&language=en&sortBy=publishedAt&pageSize=20&page=${currentPage}&apiKey=${apiKey}`;

        let response;
        try {
          response = await fetch(url, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            },
            signal: AbortSignal.timeout(30000) // 30 second timeout
          });
        } catch {
          const newsApiUrl = `https://newsapi.org/v2/everything?q=${encodeURIComponent(healthQuery)}&language=en&sortBy=publishedAt&pageSize=20&page=${currentPage}&apiKey=${apiKey}`;
          url = `https://api.allorigins.win/get?url=${encodeURIComponent(newsApiUrl)}`;
          response = await fetch(url, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            },
            signal: AbortSignal.timeout(45000) // 45 second timeout for proxy
          });
        }

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('API key is invalid or expired. Please contact support.');
          } else if (response.status === 429) {
            throw new Error('Too many requests. Please try again later.');
          } else {
            throw new Error(`Unable to fetch news (${response.status}). Please try again later.`);
          }
        }

        let data = await response.json();

        if (data.contents) {
          data = JSON.parse(data.contents);
        }

        if (data.status === 'ok' && data.articles) {
          const filteredArticles = data.articles
            .filter((article: NewsArticle) => {
              if (!article.title || !article.description || !article.url) return false;

              const healthKeywords = ['sleep', 'apnea', 'cpap', 'health', 'medical', 'therapy', 'treatment', 'disorder', 'breathing', 'respiratory'];
              const content = (article.title + ' ' + article.description).toLowerCase();
              return healthKeywords.some(keyword => content.includes(keyword));
            })
            .map((article: NewsArticle) => ({
              ...article,
              urlToImage: article.urlToImage || 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop'
            }));

          if (filteredArticles.length === 0 && currentPage === 1) {
            throw new Error('No relevant health articles found. Please try again later.');
          }

          setArticles(filteredArticles);
          setTotalResults(data.totalResults || 0);

          const totalPages = Math.ceil((data.totalResults || 0) / 20);
          setHasMorePages(currentPage < totalPages);
        } else {
          throw new Error('Invalid API response format');
        }

      } catch (err) {
        console.error('Error fetching news:', err);

        if (currentPage === 1) {
          setArticles([]);
          setError(err instanceof Error ? err.message : 'Unable to load health news. Please check your internet connection and try again.');
        } else {
          setError('Unable to load more articles. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [retryCount, currentPage]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: colors.background }}
    >
      {/* Hero Section */}
      <div className="relative pt-32 pb-8 sm:pt-40 sm:pb-12 lg:pt-48 lg:pb-16 mb-16 sm:mb-20 lg:mb-24 overflow-hidden">
        {/* Gradient background */}
        <GradientBackground position="top" height="large" />

        <div className="px-6 sm:px-8 lg:px-12 xl:px-16 relative z-10">
          <div className="max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <motion.h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
                style={{ color: colors.heading }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              >
                Sleep Health News & Research
              </motion.h1>
              <motion.p
                className="text-xl sm:text-2xl leading-relaxed max-w-4xl mx-auto"
                style={{ color: colors.text }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              >
                Stay informed with the latest sleep apnea research, medical treatments, and health news from trusted medical sources worldwide
              </motion.p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">

          {/* Loading State */}
          {loading && (
            <div className="py-8">
              <Spinner
                size="xl"
                color={colors.primary}
                className="mb-4"
              />
              <p className="text-center" style={{ color: colors.text }}>
                {currentPage === 1 ? 'Loading latest health news...' : 'Loading more articles...'}
              </p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-8">
              <div
                className="p-6 rounded-lg border-2 mx-auto max-w-md"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.error,
                  color: colors.error
                }}
              >
                <FaNewspaper className="text-3xl mx-auto mb-4" />
                <p className="text-lg font-semibold mb-2">Unable to Load Health News</p>
                <p className="text-sm mb-4">{error}</p>
                <button
                  onClick={handleRetry}
                  className="px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 hover:shadow-md"
                  style={{
                    backgroundColor: colors.primary,
                    color: 'white'
                  }}
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* News Articles */}
          {!loading && !error && (
            <section className="mt-4 mb-8 sm:mb-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {articles.map((article, index) => (
                  <motion.article
                    key={`${article.url}-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="rounded-tr-3xl rounded-bl-3xl border overflow-hidden transition-all duration-300 hover:-translate-y-1"
                    style={{
                      backgroundColor: colors.background,
                      borderColor: colors.border
                    }}
                  >
                    {/* Article Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.urlToImage}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        onError={(e) => {
                          e.currentTarget.src = 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop';
                        }}
                      />
                      <div className="absolute top-4 left-4">
                        <span
                          className="px-3 py-1 rounded-full text-xs font-semibold"
                          style={{
                            backgroundColor: colors.primary,
                            color: 'white'
                          }}
                        >
                          {article.source.name}
                        </span>
                      </div>
                    </div>

                    {/* Article Content */}
                    <div className="p-6">
                      <h3
                        className="text-lg sm:text-xl font-bold mb-3 line-clamp-2"
                        style={{ color: colors.heading }}
                      >
                        {article.title}
                      </h3>

                      <p
                        className="text-sm leading-relaxed mb-4 line-clamp-3"
                        style={{ color: colors.text }}
                      >
                        {article.description}
                      </p>

                      {/* Article Meta */}
                      <div className="flex items-center justify-between text-xs mb-4">
                        <div className="flex items-center" style={{ color: colors.text }}>
                          <FaCalendarAlt className="mr-1" />
                          {formatDate(article.publishedAt)}
                        </div>
                      </div>

                      {/* Read More Button */}
                      <TrackedAnchor
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 hover:shadow-md"
                        style={{
                          backgroundColor: colors.primary,
                          color: 'white'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = colors.primaryButton;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = colors.primary;
                        }}
                        gtmId="news_article_read_more"
                        gtmSection="page_news"
                        gtmLabel={article.title}
                        gtmExtra={{ content_title: article.title }}
                      >
                        Read More
                        <FaExternalLinkAlt className="ml-2 text-xs" />
                      </TrackedAnchor>
                    </div>
                  </motion.article>
                ))}
              </div>
            </section>
          )}

          {/* Pagination */}
          {!loading && !error && articles.length > 0 && (
            <div className="text-center mb-8 sm:mb-12">
              <div className="flex justify-center items-center space-x-4">
                <span
                  className="text-sm"
                  style={{ color: colors.text }}
                >
                  Page {currentPage} • {totalResults} total articles
                </span>
                {hasMorePages && (
                  <button
                    onClick={handleNextPage}
                    disabled={loading}
                    className="px-6 py-3 rounded-full font-semibold text-white transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    style={{ backgroundColor: colors.primary }}
                  >
                    {loading ? (
                      <Spinner
                        size="sm"
                        color="white"
                        text="Loading..."
                        showText={true}
                      />
                    ) : (
                      'Load More'
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Call to Action */}
          <section className="text-center mb-8 sm:mb-12">
            <div
              className="p-8 lg:p-12 rounded-tr-3xl rounded-bl-3xl border"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border
              }}
            >
              <h2
                className="text-3xl sm:text-4xl font-bold mb-6"
                style={{ color: colors.heading }}
              >
                Take Control of Your Sleep Health
              </h2>
              <p
                className="text-lg mb-8 max-w-3xl mx-auto"
                style={{ color: colors.text }}
              >
                Sleep apnea affects millions of people globally and is linked to serious health conditions. Stay informed with the latest medical research and take our comprehensive assessment to understand your risk factors and get personalized health recommendations.
              </p>
              <TrackedLink
                to="/assessment"
                className="px-8 py-4 rounded-full font-semibold text-white text-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 inline-block"
                style={{ backgroundColor: colors.primary }}
                gtmId="news_cta_take_assessment"
                gtmSection="page_news"
                gtmLabel="Take Sleep Assessment"
              >
                Take Sleep Assessment
              </TrackedLink>
            </div>
          </section>
        </div>
      </main>

      {/* Modals */}
      <LoginModal />
      {isAuthenticated && (
        <ProfileModal
          visible={showProfile}
          onClose={handleCloseProfile}
        />
      )}
    </div>
  );
};

export default News;
